import { useForm } from '@nerdgeschoss/react-use-form-library';
import React from 'react';
import { Button } from '../../components/Button/Button';
import { FormDatepicker } from '../../components/Form/Datepicker/FormDatepicker';
import { FormSelect } from '../../components/Form/Select/FormSelect';
import { useModal } from '../../components/Modal/PresentedModal';
import { t } from '../../locales/translate';
import { runChartExportPreview, runBookChartsExportPreview } from '../../model/exports';

interface Props {
  onCreated: () => void;
}

const countries = ['de', 'at', 'ch'] as const;
const exportTypes = ['charts', 'bookCharts'] as const;

interface Model {
  day?: Date;
  country?: (typeof countries)[number];
  exportType?: (typeof exportTypes)[number];
}

export function Preview({ onCreated }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const { model, fields, onSubmit, valid, submissionStatus } = useForm<Model>({
    model: { day: new Date(), country: 'de', exportType: 'charts' },
    onSubmit: async ({ model }) => {
      const { day, country, exportType } = model;
      if (!day || !country) return;

      if (exportType === 'charts') {
        await runChartExportPreview({ day, country });
      } else if (exportType === 'bookCharts') {
        await runBookChartsExportPreview({ day });
      }

      onCreated();
      close();
    },
  });

  return (
    <Modal
      open={open}
      title={t.model.charts.generatePreview}
      footer={
        <>
          <Button
            title={t.actions.cancel('')}
            onClick={() => {
              close();
            }}
          />
          <Button
            type="primary"
            title={t.actions.create('')}
            onClick={onSubmit}
            disabled={!valid}
            loading={submissionStatus === 'submitting'}
          />
        </>
      }
    >
      <FormDatepicker {...fields.day} disabledDays={[{ before: new Date() }]} />
      <FormSelect
        {...fields.exportType}
        options={exportTypes.map((value) => ({ value, label: t.model.chartExportTypes[value] }))}
        onChange={(value) => {
          fields.country.onChange('de');
          fields.exportType.onChange(value);
        }}
      />
      {model.exportType !== 'bookCharts' && (
        <FormSelect
          {...fields.country}
          options={countries.map((value) => ({ value, label: t.model.countries[value] }))}
        />
      )}
    </Modal>
  );
}
